import React, { FC, Fragment, ReactNode } from 'react';
import { Popover, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { APP_BG, APP_BORDER } from '@/constant/theme/colors.constant';
import { Text, Caption } from './AppText';

/* ================= TYPES ================= */
type PopoverVariant = 'default' | 'danger' | 'warning' | 'info' | 'success';

type PopoverItem = {
  name: string;
  description?: string;
  link?: string;
  url?: string;
};

interface PopoverProps {
  button: ReactNode;
  testid: string;
  title?: string;
  popOverList?: PopoverItem[];
  content?: ReactNode;
  variant?: PopoverVariant;
}

/* ================= STYLE MAP (MATCH MODAL) ================= */
const stripeGradient: Record<PopoverVariant, string> = {
  default: 'from-primary via-secondary to-tertiary',
  danger: 'from-red-500 to-red-600',
  warning: 'from-yellow-400 to-amber-500',
  info: 'from-blue-500 to-blue-600',
  success: 'from-green-500 to-green-600',
};

/* ================= COMPONENT ================= */
const PopOver: FC<PopoverProps> = ({
  button,
  testid,
  title,
  popOverList = [],
  content,
  variant = 'default',
}) => {
  return (
    <div className="relative flex items-center">
      <Popover className="relative">
        {({ open, close }) => (
          <>
            {/* ================= BUTTON ================= */}
            <Popover.Button
              data-testid={testid ?? ''}
              className={classNames(
                `
                flex items-center justify-center
                p-2 rounded-xl
                transition-all duration-200
                focus:outline-none
                `,
                open
                  ? 'bg-gray-100 dark:bg-white/10'
                  : 'hover:bg-gray-100 dark:hover:bg-white/5',
              )}
            >
              {button}
            </Popover.Button>

            {/* ================= PANEL ================= */}
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Popover.Panel
                className={classNames(
                  'absolute right-0 top-14 z-50 w-[calc(100vw-1rem)] max-w-80 rounded-2xl shadow-2xl overflow-hidden',
                  APP_BG.base,
                  'ring-1 ring-black/5 dark:ring-white/5 backdrop-blur-sm',
                )}
              >
                {/* ===== Gradient Accent Stripe ===== */}
                <div
                  className={classNames(
                    'h-1 bg-gradient-to-r',
                    stripeGradient[variant],
                  )}
                />

                {/* ================= HEADER ================= */}
                {title && (
                  <div className={`px-6 py-4 border-b ${APP_BORDER.soft}`}>
                    <Text as="h3" size="xl" weight="bold" color="default">
                      {title}
                    </Text>
                  </div>
                )}

                {/* ================= LIST ================= */}
                <div className="py-2 max-h-[60vh] overflow-y-auto">
                  {popOverList.map((item, index) => (
                    <Link
                      key={index}
                      to={item.link || '#'}
                      onClick={() => close()}
                      className="
                        flex items-center gap-3
                        px-6 py-3
                        transition-all duration-200
                        hover:bg-gray-50 dark:hover:bg-white/5
                        active:scale-[0.98]
                      "
                    >
                      {/* ICON */}
                      <div className="h-10 w-10 flex-shrink-0">
                        <img
                          src={item?.url}
                          alt={item.name}
                          className={`h-10 w-10 rounded-xl object-cover ${APP_BORDER.default}`}
                        />
                      </div>

                      {/* TEXT */}
                      <div className="flex flex-col min-w-0">
                        <Text
                          as="p"
                          size="xl"
                          weight="semibold"
                          color="default"
                        >
                          {item.name}
                        </Text>
                        {item.description && (
                          <Caption size="base" color="muted" truncate>
                            {item.description}
                          </Caption>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>

                {/* ================= CUSTOM CONTENT ================= */}
                {content && (
                  <div className={`border-t ${APP_BORDER.soft} px-6 py-4`}>
                    {content}
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default PopOver;
